<!-- Basically like a SpotEntry, but displaying Decoder data instead. -->

<template>
  <div>
    <v-list
      dense
      avatar
      class="pa-0"
      :class="{
        'bg-warning': isActiveViaTags && !entry.active,
      }"
    >
      <v-list-tile
        class="entry-values entry-tile mt-1 list-tile-align-start"
        :ripple="false"
      >
        <transition
          name="slide-x-transition"
          mode="out-in"
        >
          <div
            v-if="showSelectionCheckbox && entry.selectable"
            style="margin-top: auto; margin-bottom: auto"
          >
            <v-checkbox
              v-model="entry.selected"
              color="msr"
              class="mt-0"
              hide-details
            />
          </div>
        </transition>
        <!-- Preview for EditableTVSpotVideo -->
        <div
          v-if="entry.decoder.ctype === 'tv' && entry.spot.editable_template"
          class="editabletvspotvideo-preview"
          :class="{xs: $vuetify.breakpoint.xs}"
          style="position: relative"
          :style="{opacity: opacity}"
        >
          <mini-preview-unprocessed
            v-if="!entry.processed"
            :ctype="entry.ctype"
            show-tv-as-text
            :small="$vuetify.breakpoint.smAndUp"
          />
          <mini-preview-video
            v-else
            :spot="entry.spot"
            :tvformat="entry.decoder.tvformat"
            class="clickable"
            :small="$vuetify.breakpoint.smAndUp"
            @started="videoPreviewRunning = true"
            @stopped="videoPreviewRunning = false"
          />
        </div>
        <v-list-tile-avatar
          class="mr-3 smaller-avatar clickable"
          :style="{opacity: opacity}"
        >
          <icon-tv-decoder
            v-if="entry.decoder.ctype === 'tv'"
            style="margin-top: 6px"
            :tvdecoder="entry.decoder"
          />
          <v-icon
            v-if="entry.decoder.ctype === 'radio'"
            style="margin-bottom: 4px"
          >
            radio
          </v-icon>
        </v-list-tile-avatar>
        <!-- Description -->
        <div class="entry-item-group grow-3">
          <div class="entry-item grow-3">
            <v-list-tile-title
              class="mt-2 wrap auto-height"
              :class="{
                'msr--text':
                  showSelectionCheckbox && entry.selectable && entry.selected,
              }"
            >
              <div
                class="clickable pseudo-anchor--inverted"
                :style="{opacity: opacity}"
                @click="goToDecoder(entry.decoder)"
              >
                {{ entry.decoder.description }}
              </div>
              <!-- Matching Tags info -->
              <matching-tags
                v-if="isActiveViaTags"
                :matching-tags="entry.through_matching_tags"
                :tag-entry-active="entry.active"
                @reactivate="toggleActivateTagEntry(entry.ctype, entry)"
              />
            </v-list-tile-title>
          </div>
          <!-- Closingtime Weekdays & Time -->
          <entry-closingtime
            v-if="entry.id && entry.spot.closingtime"
            :entry="entry"
            class="entry-item grow-1 timing clickable"
            @click.native.stop="
              showActions && updateAllowed
                ? (showEntryTimingDialog = true)
                : null
            "
          />
          <!-- Timing related info -->
          <div
            v-if="showTiming"
            class="entry-item grow-1 timing"
            :class="getTimingCSS(entry, showActions)"
            @click.stop="
              showActions && updateAllowed
                ? (showEntryTimingDialog = true)
                : null
            "
          >
            <v-layout
              column
              :class="{clickable: updateAllowed}"
            >
              <!-- Start/Stopdate -->
              <small v-if="entry.has_timing">
                <span data-cy="spotdecoderentry-startdate">{{
                  entry.startdate | dayjs('DD.MM.YYYY')
                }}</span>
                <span
                  v-if="entry.stopdate !== null"
                  data-cy="spotdecoderentry-stopdate"
                >- {{ entry.stopdate | dayjs('DD.MM.YYYY') }}</span>
              </small>
              <!-- Timeframe -->
              <entry-timeframe :timeframes="entry.timeframes || []" />
            </v-layout>
          </div>
        </div>
        <!-- Actions Menu -->
        <div class="entry-item-group last mt-1">
          <v-layout
            row
            justify-end
            align-center
          >
            <v-icon
              v-if="entry.decoder.read_only"
              class="warning--text clickable"
              @click="alertReadOnly()"
            >
              lock
            </v-icon>
            <v-layout
              v-if="showStatus"
              row
              class="mr-2"
            >
              <div
                v-if="entry.hotrotation"
                data-cy="spotdecoderentry-hotrotation"
              >
                <v-icon
                  class="msr--text ml-1"
                  :class="{clickable: showActions && updateAllowed}"
                  style="font-size: 1.25em"
                  @click.stop="
                    showActions && updateAllowed
                      ? toggleEntryHotRotation(entry)
                      : null
                  "
                >
                  star
                </v-icon>
              </div>
              <div v-if="entry.has_timing">
                <v-icon
                  class="msr--text ml-1"
                  :class="{clickable: showActions && updateAllowed}"
                  style="font-size: 1.25em"
                  @click.stop="
                    showActions && updateAllowed
                      ? (showEntryTimingDialog = true)
                      : null
                  "
                >
                  timer
                </v-icon>
              </div>
            </v-layout>
            <v-menu
              v-if="showActions && atLeastOneActionVisible"
              offset-y
              left
              data-cy="spotdecoderentry-actions-menu"
              @click.native.stop
            >
              <v-icon
                slot="activator"
                class="ma-1 unselectable"
                :medium="$vuetify.breakpoint.xsOnly"
              >
                more_vert
              </v-icon>
              <v-list>
                <!-- Change timing -->
                <v-list-tile
                  v-if="showTimingAction"
                  data-cy="spotdecoderentry-action-timing"
                  @click="showEntryTimingDialog = true"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      :class="{
                        'msr--text': entry.has_timing,
                        'grey--text': !entry.has_timing,
                      }"
                    >
                      timer
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('timing') }}
                </v-list-tile>
                <!-- Toggle hotrotation -->
                <v-list-tile
                  v-if="showToggleHotRotationAction"
                  data-cy="spotdecoderentry-action-hotrotation"
                  @click="toggleEntryHotRotation(entry)"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      v-if="!entry.hotrotation"
                      class="grey--text"
                    >
                      star_outline
                    </v-icon>
                    <v-icon
                      v-if="entry.hotrotation"
                      class="msr--text mr-2"
                    >
                      star
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('hotrotation') }}
                </v-list-tile>
                <!-- Edit overlay data -->
                <v-list-tile
                  v-if="showEditOverlayDataAction"
                  data-cy="spotdecoderentry-action-overlaydata"
                  @click="showEditOverlayDataDialog = true"
                >
                  <v-list-tile-avatar>
                    <v-icon class="grey--text">
                      edit
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('customize') }}
                </v-list-tile>
                <!-- Deactivate -->
                <v-list-tile
                  v-if="showRemovalAction"
                  data-cy="spotdecoderentry-action-removal"
                  @click="
                    entryToRemove = entry;
                    showConfirmRemovalDialog = true;
                  "
                >
                  <v-list-tile-avatar>
                    <v-icon class="grey--text">
                      block
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('deactivate') }}
                </v-list-tile>
                <!-- Deactivate tagged content -->
                <v-list-tile
                  v-if="showTagEntryDeactivationAction"
                  @click="toggleActivateTagEntry(entry.ctype, entry)"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      v-if="entry.active"
                      class="grey--text"
                    >
                      block
                    </v-icon>
                    <v-icon
                      v-else
                      class="grey--text"
                    >
                      play_arrow
                    </v-icon>
                  </v-list-tile-avatar>
                  <span v-if="entry.active">{{ $t('deactivate') }}</span>
                  <span v-else>{{ $t('reactivate') }}</span>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-layout>
        </div>
      </v-list-tile>
      <!-- Overlay Data for this TVSpotEntry -->
      <v-slide-y-transition>
        <overlay-data-list-tile
          v-if="hasOverlayData"
          class="mb-2"
          :overlay-options="entry.spot.overlay_options"
          :overlay-data="entry.overlay_data"
          :disabled="!updateAllowed"
          @click="updateAllowed ? (showEditOverlayDataDialog = true) : null"
        />
      </v-slide-y-transition>
    </v-list>
    <!-- Entry Timing Dialog -->
    <entry-timing-dialog
      :show.sync="showEntryTimingDialog"
      :entries="[entry]"
      :show-timeframe-controls="!isClosingTimeRadioSpotEntry(entry)"
      @submitted="$emit('changed')"
    />
    <!-- Removal Dialog -->
    <v-dialog
      v-model="showConfirmRemovalDialog"
      absolute
    >
      <v-card>
        <v-card-title>
          <v-btn
            style="position: absolute; top: 0; right: 0"
            flat
            icon
            :title="$t('close')"
            @click="showConfirmRemovalDialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
          <div class="headline">
            {{ $t('askDeleteEntry') }}
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            data-cy="spotdecoderentry-confirm-removal-btn"
            @click="removeEntry(entryToRemove.ctype, entryToRemove)"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Edit OverlayData Dialog -->
    <edit-overlay-data-dialog
      :show.sync="showEditOverlayDataDialog"
      :entries="[entry]"
      @submitted="$emit('changed')"
    />
    <v-divider v-if="showDivider" />
  </div>
</template>

<script>
import {isClosingTimeRadioSpotEntry} from '@/mixins/identifiers.js';
import commonEntry from '@/mixins/commonEntry.js';
import spotentryUpdateOnContentProcessed from '@/mixins/spotentryUpdateOnContentProcessed.js';

import BtnOk from '@/components/ButtonOk';
import EditOverlayDataDialog from '@/components/EditOverlayDataDialog';
import EntryClosingtime from '@/components/EntryClosingtime';
import EntryTimeframe from '@/components/EntryTimeframe';
import EntryTimingDialog from '@/components/EntryTimingDialog';
import IconTvDecoder from '@/components/IconTvDecoder';
import MatchingTags from './MatchingTags.vue';
import MiniPreviewUnprocessed from '@/components/MiniPreviewUnprocessed';
import MiniPreviewVideo from '@/components/MiniPreviewVideo';
import OverlayDataListTile from '@/components/OverlayDataListTile';

export default {
  components: {
    BtnOk,
    EditOverlayDataDialog,
    EntryClosingtime,
    EntryTimeframe,
    EntryTimingDialog,
    IconTvDecoder,
    MiniPreviewUnprocessed,
    MiniPreviewVideo,
    OverlayDataListTile,
    MatchingTags,
  },
  mixins: [commonEntry, spotentryUpdateOnContentProcessed],
  props: {
    /* A Radio- or TVSpotEntry object. */
    entry: {
      type: Object,
      required: true,
    },
    /* Whether to enable the 'remove this entry' menu action. */
    removalAllowed: {
      type: Boolean,
      default: true,
    },
    /* Whether to enable the 'update hotrotation/timing' etc. menu actions. */
    updateAllowed: {
      type: Boolean,
      default: true,
    },
    /* All props below control column visibility. */
    showSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    showTiming: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showEntryTimingDialog: false,
      showEditOverlayDataDialog: false,
      videoPreviewRunning: false,
    };
  },
  computed: {
    /**
     * Fade out a few things if we have a deactivated TagEntry.
     */
    opacity() {
      if (this.isActiveViaTags && !this.entry.active) {
        return 0.5;
      }
      return 1.0;
    },
    showTimingAction() {
      return this.updateAllowed && !this.isActiveViaTags;
    },
    showToggleHotRotationAction() {
      return (
        this.updateAllowed &&
        !this.isClosingTimeRadioSpotEntry(this.entry) &&
        !this.isActiveViaTags
      );
    },
    showEditOverlayDataAction() {
      return this.updateAllowed && this.hasOverlayData;
    },
    showRemovalAction() {
      return this.removalAllowed && !this.isActiveViaTags;
    },
    showTagEntryDeactivationAction() {
      return (
        this.removalAllowed &&
        !!this.isActiveViaTags &&
        this.entry.spot.tag_entries_can_be_deactivated
      );
    },
    atLeastOneActionVisible() {
      return (
        this.showTimingAction ||
        this.showToggleHotRotationAction ||
        this.showEditOverlayDataAction ||
        this.showRemovalAction ||
        this.showTagEntryDeactivationAction
      );
    },
    overlayData() {
      return this.entry.overlay_data;
    },
    hasOverlayData() {
      return !!this.overlayData && Object.keys(this.overlayData).length > 0;
    },
    isActiveViaTags() {
      return (
        this.entry.through_matching_tags &&
        this.entry.through_matching_tags.length > 0
      );
    },
  },
  mounted() {
    this.maybeRefetchDecoderOnContentProcessedEvent();
  },
  methods: {
    isClosingTimeRadioSpotEntry: isClosingTimeRadioSpotEntry,
    goToDecoder(decoder) {
      let path = '/player/' + decoder.ctype + '/' + decoder.id;
      this.$router.push(path);
    },
  },
};
</script>

<style>
/* Ensure full width and adaptive height for v-list-tile. */
.entry-tile .v-list__tile {
  width: 100%;
  height: auto !important;
}
</style>

<style scoped>
.back-btn {
  margin: 0;
  padding: 0;
}

.menu-item {
  display: flex;
  align-items: center;
}

.entry-headers {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  height: auto;
  padding: 0;
}

.entry-values {
  display: flex;
}

.entry-item {
  margin: 0.1em 0 0.1em 0;
}
.entry-item.last {
  margin-right: 0;
}

.entry-item-group {
  display: flex;
  flex-flow: row nowrap;
}
.entry-item-group.last {
  justify-content: flex-end;
  align-items: flex-start;
}

.timing {
  display: flex;
  align-self: center;
}
@media (orientation: landscape) and (max-width: 599px) {
  .timing {
    margin-left: 1em;
  }
}

@media (max-width: 959px) {
  .entry-item-group {
    flex-flow: column nowrap;
  }
  .timing {
    align-self: auto !important;
  }
  .entry-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.editabletvspotvideo-preview {
  display: flex;
  align-items: flex-start;
  margin-right: 1em;
  margin-bottom: 1em;
}
.editabletvspotvideo-preview.xs {
  margin-right: 0.5em;
  margin-bottom: 0.5em;
}

.spot-status {
  height: 55px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
@media (max-width: 599px) {
  .spot-status {
    height: 75px;
  }
}

.decoder-subtitle {
  display: flex;
  justify-content: space-between;
}

.smaller-avatar {
  min-width: auto !important;
  width: auto !important;
  min-height: auto !important;
  height: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/SpotDecoderEntry.vue